import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { SharedModule } from 'src/shared/shared.module';

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  const scope = globalThis as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor };
  return scope.SpeechRecognition || scope.webkitSpeechRecognition || null;
}

/**
 * Header ask bar: type or speak a question about the guidebook. Speech uses the
 * browser's own recognizer (on-device on Chrome and Safari), so the mic only shows
 * where that exists.
 */
@Component({
  selector: 'guidebook-ask-bar',
  templateUrl: './guidebook-ask-bar.component.html',
  standalone: true,
  // SharedModule is here for the translate pipe the template uses. TidyUiComponentsModule
  // looks like it exports one too, but its TranslationPipe is `standalone: true` and sits
  // in that module's `declarations`, so it never actually reaches a consumer.
  imports: [CommonModule, SharedModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidebookAskBarComponent implements OnDestroy {
  readonly placeholder = input('Ask this guidebook…');
  readonly busy = input(false);
  readonly ask = output<string>();
  readonly cleared = output<void>();

  readonly text = signal('');
  readonly isListening = signal(false);
  readonly speechSupported = speechRecognitionCtor() !== null;

  private recognition: SpeechRecognitionLike | null = null;

  onInput(event: Event) {
    this.text.set((event.target as HTMLInputElement).value);
  }

  submit() {
    const question = this.text().trim();
    if (!question || this.busy()) { return; }
    this.ask.emit(question);
  }

  clear() {
    this.text.set('');
    this.stopListening();
    this.cleared.emit();
  }

  toggleListening() {
    if (this.isListening()) {
      this.stopListening();
      return;
    }
    const Ctor = speechRecognitionCtor();
    if (!Ctor) { return; }
    const recognition = new Ctor();
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = event => {
      let transcript = '';
      for (const result of Array.from(event.results)) {
        transcript += result[0]?.transcript ?? '';
      }
      this.text.set(transcript);
    };
    recognition.onend = () => {
      this.isListening.set(false);
      this.recognition = null;
      // A spoken question submits itself once the recognizer hears the pause.
      if (this.text().trim()) {
        this.submit();
      }
    };
    recognition.onerror = () => {
      this.isListening.set(false);
      this.recognition = null;
    };
    this.recognition = recognition;
    this.isListening.set(true);
    recognition.start();
  }

  ngOnDestroy() {
    this.recognition?.abort();
    this.recognition = null;
  }

  private stopListening() {
    if (!this.recognition) { return; }
    const recognition = this.recognition;
    this.recognition = null;
    recognition.onend = null;
    recognition.abort();
    this.isListening.set(false);
  }
}
